import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col, Spinner } from 'react-bootstrap';
import { FaCog, FaSave } from 'react-icons/fa';
import superadminAPI from '../../utils/superadminAPI';

const Settings = () => {
  const [settings, setSettings] = useState({
    systemName: 'FieldFlow',
    companyNameRequired: true,
    allowPersonnelSelfRegistration: false,
    defaultReportFields: 'location,description,timestamp',
    maintenanceMode: false
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  // Fetch settings on component mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        // Uncomment when API is ready
        // const response = await axios.get(`${API_BASE_URL}/superadmin/settings`);
        // setSettings(response.data);
        
        // For now, just use default settings
        setTimeout(() => {
          setLoading(false);
        }, 500);
      } catch (err) {
        setError('Failed to load settings. Please try again.');
        setLoading(false);
      }
    };
    
    fetchSettings();
  }, []);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings({
      ...settings,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      // Uncomment when API is ready
      // await axios.put(`${API_BASE_URL}/superadmin/settings`, settings);
      
      // For now, just simulate API call
      setTimeout(() => {
        setSuccess(true);
        setLoading(false);
        
        // Hide success message after 3 seconds
        setTimeout(() => {
          setSuccess(false);
        }, 3000);
      }, 1000);
    } catch (err) {
      setError('Failed to update settings. Please try again.');
      setLoading(false);
    }
  };
  
  if (loading && !settings.systemName) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }
  
  return (
    <Container fluid className="py-3 py-md-4">
      <Row className="mb-3 mb-md-4">
        <Col>
          <h1 className="mb-2">System Settings</h1>
          <p className="text-muted mb-0">Configure system-wide settings for the FieldFlow platform.</p>
        </Col>
      </Row>
      
      {success && (
        <Alert variant="success" className="mb-3 mb-md-4">
          <FaSave className="me-2" />
          Settings updated successfully!
        </Alert>
      )}
      
      {error && (
        <Alert variant="danger" className="mb-3 mb-md-4">
          <FaCog className="me-2" />
          {error}
        </Alert>
      )}
      
      <Row>
        <Col lg={8} xl={9}>
          <Card className="shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <FaCog className="me-2 text-primary" />
                General Settings
              </h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>System Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="systemName"
                        value={settings.systemName}
                        onChange={handleChange}
                        required
                        placeholder="Enter system name"
                      />
                      <Form.Text className="text-muted">
                        This name will appear in the application title and emails.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Default Report Fields</Form.Label>
                      <Form.Control
                        type="text"
                        name="defaultReportFields"
                        value={settings.defaultReportFields}
                        onChange={handleChange}
                        placeholder="location,description,timestamp"
                      />
                      <Form.Text className="text-muted">
                        Comma-separated list of default fields for reports.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row className="mb-3">
                  <Col xs={12} md={4}>
                    <Form.Check
                      type="switch"
                      id="companyNameRequired"
                      name="companyNameRequired"
                      label="Require Company Name"
                      checked={settings.companyNameRequired}
                      onChange={handleChange}
                      className="mb-2"
                    />
                    <Form.Text className="text-muted small">
                      If enabled, company name will be required for all company admins.
                    </Form.Text>
                  </Col>
                  
                  <Col xs={12} md={4}>
                    <Form.Check
                      type="switch"
                      id="allowPersonnelSelfRegistration"
                      name="allowPersonnelSelfRegistration"
                      label="Allow Personnel Self-Registration"
                      checked={settings.allowPersonnelSelfRegistration}
                      onChange={handleChange}
                      className="mb-2"
                    />
                    <Form.Text className="text-muted small">
                      If enabled, personnel can register themselves.
                    </Form.Text>
                  </Col>
                  
                  <Col xs={12} md={4}>
                    <Form.Check
                      type="switch"
                      id="maintenanceMode"
                      name="maintenanceMode"
                      label="Maintenance Mode"
                      checked={settings.maintenanceMode}
                      onChange={handleChange}
                      className="mb-2"
                    />
                    <Form.Text className="text-muted small">
                      If enabled, only super admins can access the system.
                    </Form.Text>
                  </Col>
                </Row>
                
                <div className="d-flex justify-content-end flex-column flex-md-row gap-2">
                  <Button 
                    variant="secondary" 
                    type="button"
                    className="w-100 w-md-auto"
                    onClick={() => window.location.reload()}
                  >
                    Reset to Defaults
                  </Button>
                  <Button 
                    variant="primary" 
                    type="submit" 
                    disabled={loading}
                    className="w-100 w-md-auto"
                  >
                    {loading ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        Save Settings
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4} xl={3} className="mt-4 mt-lg-0">
          <Card className="shadow-sm bg-light">
            <Card.Body>
              <h5 className="mb-3">Settings Information</h5>
              <p className="small">
                These settings affect the entire FieldFlow platform and all users. Changes will be applied immediately.
              </p>
              <ul className="small">
                <li><strong>System Name:</strong> Used in titles and notifications</li>
                <li><strong>Report Fields:</strong> Default fields for new reports</li>
                <li><strong>Company Name:</strong> Makes company name mandatory</li>
                <li><strong>Self-Registration:</strong> Allows personnel to sign up</li>
                <li><strong>Maintenance Mode:</strong> Restricts access during maintenance</li>
              </ul>
              <p className="mb-0 text-muted small">
                <strong>Note:</strong> Some settings may require a system restart to take full effect.
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Settings;
